import { defineStore } from 'pinia'
import { config } from '@/core/config'
import { SanitizeValue } from '@/core/utils'
import { useSearchQueryStore } from '@/store/SearchQueryStore'
import { searchErrorLogger, searchLogger } from '@/core/Logger'
import { SmartSearchConfig } from '@/main'
import { resolveCustomSmartSearchConfig } from '@/core/customSmartSearchConfig'

export interface AggregationSuggestionItem {
  id: string
  label: string
  value: string
  count?: number
  path: string[]
  displayLabel: string
}

export interface AutocompleteAggregation {
  field: string
  label: string
  items: AggregationSuggestionItem[]
}

type AutocompleteAggregationField = string

type RawAggregationValue = {
  id?: string | number
  label?: string
  value?: string | number
  count?: number
  aggregations?: RawAggregationValue[]
  values?: RawAggregationValue[]
}

type RawAggregation = {
  field?: string
  label?: string
  data?: { values?: RawAggregationValue[] }
}

function toDisplayLabel(path: string[]): string {
  return path.join(' / ')
}

function normaliseValue(value: string | number | undefined, fallback: string): string {
  if (typeof value === 'number') {
    return value.toString()
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return value
  }

  return fallback
}

function flattenAggregationValues(values: RawAggregationValue[] | undefined, parentPath: string[] = []): AggregationSuggestionItem[] {
  if (!Array.isArray(values) || values.length === 0) {
    return []
  }

  const results: AggregationSuggestionItem[] = []

  values.forEach((entry) => {
    const rawLabel = typeof entry.label === 'string' && entry.label.trim().length > 0
      ? entry.label.trim()
      : typeof entry.value === 'string' && entry.value.trim().length > 0
        ? entry.value.trim()
        : typeof entry.value === 'number'
          ? entry.value.toString()
          : ''

    const path = rawLabel ? [...parentPath, rawLabel] : parentPath

    const nestedSource = Array.isArray(entry.aggregations) && entry.aggregations.length > 0
      ? entry.aggregations
      : Array.isArray(entry.values) && entry.values.length > 0
        ? entry.values
        : []

    if (nestedSource.length > 0) {
      results.push(...flattenAggregationValues(nestedSource, path))
    } else if (rawLabel) {
      const value = normaliseValue(entry.value, rawLabel)
      const id = entry.id ?? value ?? toDisplayLabel(path)
      results.push({
        id: String(id),
        label: rawLabel,
        value,
        count: typeof entry.count === 'number' ? entry.count : undefined,
        path,
        displayLabel: toDisplayLabel(path)
      })
    }
  })

  const unique = new Map<string, AggregationSuggestionItem>()
  results.forEach((item) => {
    const key = `${item.value}::${item.displayLabel}`
    if (!unique.has(key)) {
      unique.set(key, item)
    }
  })

  return Array.from(unique.values())
}

function getLabelKey(label?: string): string | null {
  if (typeof label !== 'string') {
    return null
  }

  const normalized = label.trim().toLowerCase()
  return normalized.length > 0 ? normalized : null
}

function deduplicateByDisplayLabel(items: AggregationSuggestionItem[]): AggregationSuggestionItem[] {
  if (!Array.isArray(items) || items.length === 0) {
    return []
  }

  const result: AggregationSuggestionItem[] = []
  const seen = new Map<string, number>()

  items.forEach((item) => {
    const labelKey = getLabelKey(item.displayLabel ?? item.label)
    if (!labelKey) {
      result.push(item)
      return
    }

    const existingIndex = seen.get(labelKey)
    if (existingIndex === undefined) {
      seen.set(labelKey, result.length)
      result.push(item)
      return
    }

    const existing = result[existingIndex]
    const existingCount = typeof existing.count === 'number' ? existing.count : -1
    const currentCount = typeof item.count === 'number' ? item.count : -1

    if (currentCount > existingCount) {
      result[existingIndex] = item
    }
  })

  return result
}

function sortByCountAndLabel(items: AggregationSuggestionItem[]): AggregationSuggestionItem[] {
  if (!Array.isArray(items) || items.length === 0) {
    return []
  }

  return [...items].sort((a, b) => {
    const countA = typeof a.count === 'number' ? a.count : -1
    const countB = typeof b.count === 'number' ? b.count : -1

    if (countA !== countB) {
      return countB - countA
    }

    const labelA = (a.displayLabel ?? a.label ?? '').toString()
    const labelB = (b.displayLabel ?? b.label ?? '').toString()
    return labelA.localeCompare(labelB, 'pl-PL', { sensitivity: 'base' })
  })
}

function getSuggestionLimit(): number {
  const suggestionsCount = Number(SmartSearchConfig.value?.autocomplete?.suggestions?.count)
  return Number.isFinite(suggestionsCount) && suggestionsCount > 0
    ? suggestionsCount
    : Number.POSITIVE_INFINITY
}

function filterAllowedAggregations(aggregations: RawAggregation[]): AutocompleteAggregation[] {
  if (!Array.isArray(aggregations)) {
    return []
  }

  const allowedFields = new Set<string>(resolveCustomSmartSearchConfig().autocompleteAggregationFields)

  return aggregations
    .filter((aggregation): aggregation is RawAggregation & { field: string } => {
      return typeof aggregation?.field === 'string' && allowedFields.has(aggregation.field)
    })
    .map((aggregation) => {
      const field = aggregation.field as string
      const suggestionLimit = getSuggestionLimit()
      const items = sortByCountAndLabel(
        deduplicateByDisplayLabel(
          flattenAggregationValues(aggregation.data?.values)
            .map((item) => {
              if (field !== 'categories') {
                return item
              }

              const segmentsFromPath = item.path?.length ? item.path : [item.displayLabel]
              const flattenedSegments = segmentsFromPath
                .flatMap((segment) => segment.split(/\s*[>/]\s*/))
                .map((segment) => segment.trim())
                .filter((segment) => segment.length > 0)

              const displayLabel = flattenedSegments.length > 0
                ? flattenedSegments[flattenedSegments.length - 1]
                : item.displayLabel

              return {
                ...item,
                displayLabel
              }
            })
        )
      ).slice(0, suggestionLimit)
      const label = typeof aggregation.label === 'string' && aggregation.label.trim().length > 0
        ? aggregation.label
        : field

      return {
        field,
        label,
        items
      }
    })
    .filter((aggregation) => aggregation.items.length > 0)
}

export const useAutocompleteAggregationsStore = defineStore('AutocompleteAggregationsStore', {
  state: () => ({
    aggregations: [] as AutocompleteAggregation[],
    loading: false,
    lastFetchedQuery: '',
    requestSeq: 0
  }),
  getters: {
    displayAggregations(state): AutocompleteAggregation[] {
      return state.aggregations
    },
    getAggregationByField: (state) => (field: AutocompleteAggregationField) => {
      return state.aggregations.find((aggregation) => aggregation.field === field) ?? null
    }
  },
  actions: {
    reset() {
      this.aggregations = []
      this.lastFetchedQuery = ''
    },
    async fetchAggregationsForCurrentQuery() {
      const allowedFields = resolveCustomSmartSearchConfig().autocompleteAggregationFields

      if (!Array.isArray(allowedFields) || allowedFields.length === 0) {
        this.reset()
        return
      }

      const queryStore = useSearchQueryStore()
      const sanitizedQuery = SanitizeValue(queryStore.searchQuery)

      if (sanitizedQuery.length < 3) {
        this.reset()
        return
      }

      if (sanitizedQuery === this.lastFetchedQuery && this.aggregations.length > 0) {
        return
      }

      const url = new URL(`https://${config.apiHost}/restapi/${config.customerSymbol}/search/${config.searchId}/aggregations`)
      url.searchParams.set('q', sanitizedQuery)

      const currentSeq = ++this.requestSeq

      try {
        this.loading = true
        searchLogger.log('Fetching autocomplete aggregations', sanitizedQuery)

        const response = await fetch(url.toString(), {
          headers: {
            'Api-key': config.apiKey
          },
          credentials: 'include'
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch autocomplete aggregations: ${response.status}`)
        }

        const payload = await response.json()
        const rawAggregations = Array.isArray(payload?.aggregations) ? payload.aggregations as RawAggregation[] : []
        const processed = filterAllowedAggregations(rawAggregations)

        if (currentSeq === this.requestSeq) {
          this.aggregations = processed
          this.lastFetchedQuery = sanitizedQuery
        }
      } catch (error) {
        if (currentSeq === this.requestSeq) {
          this.reset()
        }
        searchErrorLogger.log('Error fetching autocomplete aggregations', error)
      } finally {
        if (currentSeq === this.requestSeq) {
          this.loading = false
        }
      }
    }
  }
})
